// AlphaWarningBanner — α 期データ削除警告 (2026-05-23 W2 公開向け).
//
// 目的: α テスト期間中は DB スキーマ変更・運用調整等により「ユーザーが保存した
// データが予告なく消える」可能性がある. 2026-05-23 21:00 の W2 公開で新規ユーザ
// が大量流入する想定なので、データ削除可能性を事前告知してクレーム回避 + α
// テスター意識を醸成する.
//
// 構成:
//   1. `AlphaWarningBanner` — トップ画面 (HomePage) 上部に出す常設バナー.
//      黄色系の警告色 + 「× 閉じる」ボタン無し (= α 期間中は常時表示).
//      `AnnounceBanner` は WS イベント (`system.announce.posted`) 駆動の汎用
//      告知系で dismiss 可能・絶対配置だが、本コンポーネントは静的告知用に
//      別物として用意する (= 用途・配置・dismiss 挙動が違うので流用しない).
//   2. `AlphaDataWarningNotice` — 各保存画面 (ルーム作成 form / 設定モーダル /
//      ログイン form / 入室 form 等) の「保存」「作成」「登録」ボタン直前 or
//      直上に挿入する静的注釈テキスト. `text-muted` 系の控えめスタイル.
//
// α 期終了 (= β 公開 or データ保証期間突入) 時には本ファイル単体を削除すれば
// 全画面から告知が消える設計. 各画面側は import + 1 行追加のみで影響範囲を
// 局所化.
//
// 文面はユーザー確定済 (= 変更不可、本コンポーネント内にハードコード).

/**
 * HomePage タイトル「タカミンお絵描きチャット」の直下に出す α 期警告.
 *
 * 配置の変更経緯 (= 2026-05-23 改訂):
 *   初版は画面最上部の帯バナー (= bg-amber-500 横幅フル) としていたが、
 *   ユーザの実機確認で「パネル中央のコンテンツから離れすぎていて視線が
 *   繋がらない / 目立たない」とフィードバック. タイトル直下のインライン
 *   注釈に変更し、ユーザの最初の操作 (= ルーム作成ボタン) と地続きに
 *   読める位置に置く.
 *
 * スタイル: `text-amber-400` + ⚠️ アイコン、背景は無し (= パネル内に
 * 自然に溶け込む)、小さめ centered. `×` 閉じるボタンなし (= α 期間中は
 * 常時表示).
 *
 * a11y: `role="status"` + `aria-live="polite"` で初回読み上げ。
 */
export const AlphaWarningBanner = (): React.ReactNode => (
  <p
    role="status"
    aria-live="polite"
    aria-label="アルファテスト警告"
    className="-mt-3 mb-4 text-center text-sm font-bold text-amber-400"
  >
    {'⚠️'} α テスト中: データは予告なく削除される場合があります
  </p>
);

/**
 * 各保存画面の「保存」「作成」「登録」ボタン直前 or 直上に挿入する静的注釈.
 * 控えめな text-muted スタイル (= 主導線を邪魔しないが、保存操作の直前で
 * ユーザーが気付ける位置). 「× 閉じる」等のインタラクションは無し.
 *
 * 配置例:
 *   - CreateRoomPage: 「作成」ボタンの直上 (= submit 前最後の確認位置).
 *   - RoomSettingsModal: モーダル冒頭 (= 各 field の保存ボタン全体に効く).
 *   - LoginPage: フォーム下部 (= ログイン → 認証情報保存の直前).
 *   - ReJoinScreen: 「入室」ボタンの直上 (= uid 紐付くデータ作成の直前).
 *
 * a11y: `role="note"` で「補助情報」と明示. `aria-live` は付けない (= 静的な
 * 注釈なのでフォーカス遷移時の読み上げに任せる).
 */
export const AlphaDataWarningNotice = (): React.ReactNode => (
  <p
    role="note"
    className="mb-2 text-center text-sm font-bold text-amber-400"
    data-testid="alpha-data-warning-notice"
  >
    {'⚠️'} α テスト中のため、保存いただいたデータが予告なく削除される場合があります
  </p>
);
